import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Clock, Trash2, Trash } from 'lucide-react';
import { getHistory, clearHistory, deleteHistoryItem } from '../services/api';
import { AnalysisResult } from './AnalysisResult';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"

const History = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const data = await getHistory();
      setHistory(data);
    } catch (error) {
      console.error("Failed to load history", error);
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  const handleClearAll = async () => {
    try {
      await clearHistory();
      setHistory([]);
      toast.success("History cleared successfully");
    } catch (error) {
      toast.error("Failed to clear history");
    }
  };

  const handleDeleteItem = async (id) => {
    try {
      await deleteHistoryItem(id);
      setHistory(prev => prev.filter(item => item._id !== id));
      toast.success("Item deleted");
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 font-sans p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 flex items-center justify-between">
            <Link to="/" className="inline-flex items-center text-gray-500 hover:text-primary transition-colors font-medium text-sm">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Link>
            
            {history.length > 0 && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button className="inline-flex items-center text-red-500 hover:text-red-600 transition-colors font-medium text-sm px-3 py-1.5 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30">
                        <Trash2 className="w-4 h-4 mr-2" /> Clear History
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This action cannot be undone. This will permanently delete your entire verification history from our servers.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleClearAll} className="bg-red-600 hover:bg-red-700">Delete All</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
            )}
        </div>

        <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
        >
            <Clock className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-4xl font-heading font-bold mb-4">Verification History</h1>
            <p className="text-gray-500 dark:text-gray-400 text-lg">Your recently analyzed claims.</p>
        </motion.div>

        {loading ? (
            <div className="text-center text-gray-500 animate-pulse">Loading history...</div>
        ) : history.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700">
                <Clock className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500">No history found. Start scanning!</p>
            </div>
        ) : (
            <div className="space-y-8">
                <AnimatePresence>
                {history.map((item) => (
                    <motion.div 
                        key={item._id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -100, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm relative group"
                    >
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <span className="text-xs font-mono text-gray-400">{new Date(item.created_at).toLocaleString()}</span>
                                <h3 className="text-lg font-bold mt-1 line-clamp-2 pr-8">
                                    "{item.sources?.[0]?.title || "Claim Analysis"}"
                                </h3> 
                            </div>
                            <button 
                                onClick={() => handleDeleteItem(item._id)}
                                className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-all opacity-0 group-hover:opacity-100"
                                title="Delete Item"
                            >
                                <Trash className="w-4 h-4" />
                            </button>
                        </div>
                        <AnalysisResult result={item} />
                    </motion.div>
                ))}
                </AnimatePresence>
            </div>
        )}
      </div>
    </div>
  );
};

export default History;
